import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  PageHeader,
  Skeleton,
  Descriptions,
  Result,
  Typography,
  Table,
  Divider
} from 'antd';
import history from '@utils/history';
import RegionsActions, { RegionsSelectors } from '@redux/RegionsRedux';
import startCase from 'lodash-es/startCase';
import RegionChart from './chart';
import AssignHK from './assignHK';

const { Text } = Typography;

const renderPropertyName = (name, row) => {
  const { propertyID } = row;
  return (
    <Link to={`/properties/${propertyID}`}>
      <Text strong>{name}</Text>
    </Link>
  );
};

const renderBlockDays = days => {
  if (!days || !days.length) {
    return null;
  }

  return days.map((day, index) => (
    <span key={day}>
      {index > 0 && <Divider type="vertical" style={{ margin: '0 6px' }} />}
      <Text>{startCase(day)}</Text>
    </span>
  ));
};

const renderHkPreferences = housekeepers => {
  if (!housekeepers || !housekeepers[0].firstName) {
    return null;
  }
  return housekeepers.map((hk, index) => (
    <span key={hk.lastName}>
      {index > 0 && <Divider type="vertical" style={{ margin: '0 6px' }} />}
      <Text>{hk.firstName + ' ' + hk.lastName}</Text>
    </span>
  ));
};

function ViewRegionPage(props) {
  const {
    loading,
    regionData,
    loadRegion,
    resetRegion,
    match: { params }
  } = props;

  const [visible, setVisible] = useState(false);
  const [property, setProperty] = useState({});
  const setVisibleHandler = visible => setVisible(visible);

  const getProperty = propertyID =>
    properties.filter(p => p.propertyID == propertyID)[0];

  const renderEditButton = propertyId => {
    return (
      <Button
        type="primary"
        onClick={() => {
          const selProperty = getProperty(propertyId);
          setProperty(selProperty);
          setVisible(true);
        }}
      >
        Edit
      </Button>
    );
  };

  const propertyColumns = [
    {
      title: 'Property Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      render: renderPropertyName
    },
    {
      title: 'Property ID',
      dataIndex: 'propertyID',
      key: 'propertyID',
      width: '10%'
    },
    {
      title: 'Total Users',
      dataIndex: 'totalUsers',
      key: 'totalUsers'
    },
    {
      title: 'Blocked Days',
      dataIndex: 'blockedDays',
      key: 'blockedDays',
      render: renderBlockDays
    },
    {
      title: 'Housekeeper Preferences',
      dataIndex: 'housekeepers',
      key: 'housekeepers',
      render: renderHkPreferences
    },
    {
      title: 'Edit Preferences',
      dataIndex: 'propertyID',
      key: 'editPreferences',
      render: renderEditButton
    }
  ];

  const { id: regionId } = params || {};

  useEffect(() => {
    loadRegion(regionId);

    return () => {
      resetRegion();
    };
  }, [regionId]);

  const render404 = () => {
    return (
      <div className="regions main-page">
        <PageHeader
          title="Region"
          style={{
            border: '1px solid rgb(235, 237, 240)'
          }}
          onBack={() => history.goBack()}
        />

        <div className="Regions main-content">
          <Result
            status="403"
            title="403"
            subTitle="Sorry, you are not authorized to access this page."
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="regions main-page">
        <PageHeader
          style={{
            border: '1px solid rgb(235, 237, 240)'
          }}
          onBack={() => history.goBack()}
        />

        <div className="coupon main-content">
          <Skeleton />
        </div>
      </div>
    );
  }

  const {
    regionID: loadedId,
    name: title,
    officeID,
    officeName,
    timezone,
    properties,
    regionHours,
    housekeepers
  } = regionData || {};

  if (!loadedId) {
    return render404();
  }

  const hkCount = housekeepers.length;

  return (
    <div className="regions main-page">
      <PageHeader
        title={title}
        style={{
          border: '1px solid rgb(235, 237, 240)'
        }}
        onBack={() => history.goBack()}
      >
        <div
          className="content"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              width: '40%'
            }}
          >
            <Descriptions size="small" column={2}>
              <Descriptions.Item label="Office">{officeName}</Descriptions.Item>
              <Descriptions.Item label="Region ID">
                {loadedId}
              </Descriptions.Item>
              <Descriptions.Item label="Offce ID">{officeID}</Descriptions.Item>
              <Descriptions.Item label="Timezone">{timezone}</Descriptions.Item>
              <Descriptions.Item label="Regional Housekeepers">
                {hkCount}
              </Descriptions.Item>
            </Descriptions>
          </div>
          <RegionChart data={regionHours} />
        </div>
      </PageHeader>
      <div className="main-content">
        <div className="extra_content" style={{ paddingTop: 16 }}>
          <Card title="Properties">
            <Table
              rowKey="propertyID"
              dataSource={properties}
              columns={propertyColumns}
              pagination={false}
            />
          </Card>
          <AssignHK
            property={property}
            visible={visible}
            setVisible={setVisibleHandler}
          />
        </div>
      </div>
    </div>
  );
}

ViewRegionPage.propTypes = {
  match: PropTypes.object,
  loadRegion: PropTypes.func,
  resetRegion: PropTypes.func,
  loading: PropTypes.bool,
  regionData: PropTypes.object,
  regionHours: PropTypes.object
};

const mapDispatchToProps = dispatch => ({
  loadRegion: regionId => {
    dispatch(RegionsActions.loadRegionRequest(regionId));
  },
  resetRegion: () => {
    dispatch(RegionsActions.setRegionData(null));
  }
});

const mapStateToProps = state => ({
  loading: RegionsSelectors.selectLoading(state),
  regionData: RegionsSelectors.selectRegionData(state)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ViewRegionPage);
